import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Edit2, Trash2, X, AlertCircle, CheckCircle, Upload, Star, DollarSign, TrendingUp, Download, MessageSquare } from 'lucide-react';

const INPUT_CLS = 'w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all';
const LABEL_CLS = 'block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5';

/* Format large numbers nicely */
const fmtNum = (n) => {
  if (n == null) return '—';
  const v = parseFloat(n);
  if (isNaN(v)) return n;
  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  if (v >= 1_000_000)     return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1_000)         return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return v.toLocaleString();
};

const AppsListing = () => {
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingApp, setEditingApp] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '', amount: '', profit: '', downloads: '', reviews: '', rating: '', appImage: null,
  });
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editMessage, setEditMessage] = useState(null);
  const [editMessageType, setEditMessageType] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;
    axios.get('/api/apps', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setApps(r.data); setLoading(false); })
      .catch(e => { setError(e); setLoading(false); });
  }, [token]);

  const openEditPopup = (app) => {
    setEditingApp(app);
    setEditFormData({
      name: app.name || '',
      amount: app.amount != null ? app.amount.toString() : '',
      profit: app.profit != null ? app.profit.toString() : '',
      downloads: app.downloads || '',
      reviews: app.reviews || '',
      rating: app.rating || '',
      appImage: null,
    });
    setEditImagePreview(app.image || null);
    setEditMessage(null);
    setEditMessageType(null);
    setIsEditPopupOpen(true);
  };

  const closeEditPopup = () => {
    setIsEditPopupOpen(false);
    setEditingApp(null);
    setEditImagePreview(null);
    setEditMessage(null);
  };

  const handleDeleteApp = async (appId) => {
    if (!token) return;
    try {
      await axios.delete(`/api/apps/${appId}`, { headers: { Authorization: `Bearer ${token}` } });
      setApps(apps.filter(a => a._id !== appId));
    } catch (err) {
      console.error('Error deleting app:', err.response?.data || err.message);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData(p => ({ ...p, appImage: file }));
      setEditImagePreview(URL.createObjectURL(file));
    } else {
      setEditFormData(p => ({ ...p, appImage: null }));
      setEditImagePreview(editingApp?.image || null);
    }
    setEditMessage(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingApp || !token) return;
    setIsEditSubmitting(true);
    setEditMessage(null);

    const fd = new FormData();
    ['name','amount','profit','downloads','reviews','rating'].forEach(k => fd.append(k, editFormData[k]));
    if (editFormData.appImage) fd.append('appImage', editFormData.appImage);

    try {
      const res = await axios.put(`/api/apps/${editingApp._id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      setEditMessage('App updated successfully!');
      setEditMessageType('success');
      setApps(apps.map(a => a._id === editingApp._id ? (res.data.app || res.data) : a));
      setTimeout(closeEditPopup, 1600);
    } catch (err) {
      setEditMessage(err.response?.data?.msg || err.message);
      setEditMessageType('error');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  if (loading) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4">
      {[...Array(8)].map((_, i) => <div key={i} className="rounded-2xl bg-white/4 border border-white/6 animate-pulse h-52" />)}
    </div>
  );
  if (error) return (
    <div className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm mt-4">
      <AlertCircle size={14} /> Error: {error.message}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">App Listing</h1>
          <p className="text-sm text-gray-500 mt-0.5">{apps.length} app{apps.length !== 1 ? 's' : ''} configured</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {apps.map(app => {
          const profitRate = app.amount ? Math.min(((app.profit / app.amount) * 100), 100) : 0;
          return (
            <div key={app._id} className="flex flex-col rounded-2xl bg-[#0d1526]/80 border border-white/8 hover:border-white/14 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30 transition-all duration-200 overflow-hidden">

              {/* Card header */}
              <div className="flex items-center gap-3 p-4 pb-3">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/8 overflow-hidden shrink-0 flex items-center justify-center">
                  {app.image
                    ? <img src={app.image} alt={app.name} className="w-full h-full object-cover" />
                    : <span className="text-gray-500 text-base font-bold">{(app.name || '?').charAt(0)}</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-white truncate">{app.name}</h2>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Star size={10} className="text-yellow-400 fill-yellow-400 shrink-0" />
                    <span className="text-[11px] text-gray-500 truncate">{fmtNum(app.rating)}</span>
                  </div>
                </div>
                {/* Always-visible action buttons */}
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => openEditPopup(app)}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all"
                    title="Edit"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(app._id)}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="mx-4 border-t border-white/5" />

              {/* Stats */}
              <div className="px-4 py-3 space-y-2 flex-1">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <DollarSign size={11} /> Amount
                  </span>
                  <span className="text-xs font-semibold text-white">${app.amount ?? '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <TrendingUp size={11} /> Profit
                  </span>
                  <span className="text-xs font-semibold text-green-400">+${app.profit ?? '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <Download size={11} /> Downloads
                  </span>
                  <span className="text-xs text-gray-300">{app.downloads || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <MessageSquare size={11} /> Reviews
                  </span>
                  <span className="text-xs text-gray-300">{fmtNum(app.reviews)}</span>
                </div>
              </div>

              {/* Profit bar footer */}
              <div className="px-4 pb-4 pt-2">
                <div className="flex justify-between text-[10px] text-gray-600 mb-1.5">
                  <span>Profit rate</span>
                  <span>{app.amount ? ((app.profit / app.amount) * 100).toFixed(1) : 0}%</span>
                </div>
                <div className="h-1 rounded-full bg-white/5">
                  <div
                    className="h-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500"
                    style={{ width: `${Math.min(profitRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {apps.length === 0 && (
        <div className="text-center py-20 text-gray-600 text-sm">No apps yet.</div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold mb-1.5">Delete App</h3>
            <p className="text-gray-400 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDeleteApp(deleteConfirmId)} className="flex-1 py-2.5 rounded-xl bg-red-500/12 border border-red-500/25 text-red-400 text-sm font-semibold hover:bg-red-500/20 transition-all">
                Delete
              </button>
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/8 transition-all">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {isEditPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-bold text-white">Edit App</h2>
                <button onClick={closeEditPopup} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/8 transition-all">
                  <X size={16} />
                </button>
              </div>

              {editMessage && (
                <div className={`mb-4 flex items-center gap-2 p-3 rounded-xl text-sm border ${
                  editMessageType === 'success'
                    ? 'bg-green-500/10 border-green-500/20 text-green-400'
                    : 'bg-red-500/10 border-red-500/20 text-red-400'
                }`}>
                  {editMessageType === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                  {editMessage}
                </div>
              )}

              <form onSubmit={handleEditSubmit} className="space-y-4">
                {/* Image */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                    {editImagePreview
                      ? <img src={editImagePreview} alt="Preview" className="w-full h-full object-cover" />
                      : <Upload size={18} className="text-gray-600" />
                    }
                  </div>
                  <label className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/8 transition-all">
                    <Upload size={13} /> Change Image
                    <input type="file" accept="image/*" onChange={handleEditImageChange} className="sr-only" />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL_CLS}>App Name</label>
                    <input type="text" value={editFormData.name} onChange={e => setEditFormData(p => ({...p, name: e.target.value}))} className={INPUT_CLS} required />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Amount ($)</label>
                    <input type="number" step="0.01" value={editFormData.amount} onChange={e => setEditFormData(p => ({...p, amount: e.target.value}))} className={INPUT_CLS} required />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Profit ($)</label>
                    <input type="number" step="0.01" value={editFormData.profit} onChange={e => setEditFormData(p => ({...p, profit: e.target.value}))} className={INPUT_CLS} required />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Downloads</label>
                    <input type="number" value={editFormData.downloads} onChange={e => setEditFormData(p => ({...p, downloads: e.target.value}))} className={INPUT_CLS} required />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Reviews</label>
                    <input type="number" value={editFormData.reviews} onChange={e => setEditFormData(p => ({...p, reviews: e.target.value}))} className={INPUT_CLS} required />
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL_CLS}>Rating</label>
                    <input type="number" min="0" max="5000" value={editFormData.rating} onChange={e => setEditFormData(p => ({...p, rating: e.target.value}))} className={INPUT_CLS} required />
                  </div>
                </div>

                <div className="flex gap-3 pt-1">
                  <button type="submit" disabled={isEditSubmitting} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-all shadow-lg shadow-cyan-500/15">
                    {isEditSubmitting ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button type="button" onClick={closeEditPopup} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/8 transition-all">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppsListing;
